import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ShoppingCart, Check, Star, Package, Loader2,
  ChevronDown, ChevronUp, MapPin, ExternalLink, Minus, Plus, Fuel
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRODUCT_CATEGORY_LABELS = {
  meal_plan: 'Meal Plans',
  meal: 'Individual Meals',
  grocery_pack: 'Grocery Packs',
  fuel_voucher: 'Fuel Vouchers',
  ride_voucher: 'Ride Vouchers',
  transit_pass: 'Transit Passes',
  intercity: 'Intercity Travel',
  health_plan: 'Health Plans',
  fitness: 'Fitness & Wellness',
  life_insurance: 'Life Insurance',
  rent_service: 'Rent Services',
  savings: 'Savings Plans',
  subscription: 'Subscriptions',
  course_credit: 'Course Credits',
  store_credit: 'Store Credits',
  entertainment: 'Entertainment',
  general: 'Products',
};

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function VendorDetailPage() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    loadVendor();
  }, [vendorId]);

  const loadVendor = async () => {
    try {
      const token = localStorage.getItem('wallet_token');
      const res = await fetch(`${API_URL}/api/employee/vendors/${vendorId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setVendor(data);
      } else {
        toast.error('Vendor not found');
        navigate(-1);
      }
    } catch (err) {
      toast.error('Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  const getQty = (productId) => quantities[productId] || 1;
  const setQty = (productId, val) => setQuantities(prev => ({ ...prev, [productId]: Math.max(1, val) }));

  const handlePurchase = async (product) => {
    const qty = getQty(product.id);
    setPurchasing(product.id);
    try {
      const token = localStorage.getItem('wallet_token');
      const res = await fetch(`${API_URL}/api/employee/vendors/${vendorId}/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ product_id: product.id, quantity: qty }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || `Purchased ${product.name}!`, {
          description: `${fmt(data.total)} deducted from your ${data.wallet_type} wallet. Balance: ${fmt(data.new_balance)}`,
        });
      } else {
        toast.error(data.detail || 'Purchase failed');
      }
    } catch (err) {
      toast.error('Purchase failed. Please try again.');
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
    </div>
  );

  if (!vendor) return null;

  const productGroups = vendor.product_groups || {};

  return (
    <div className="space-y-5 pb-8" data-testid="vendor-detail-page">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-stone-100 transition-colors" data-testid="vendor-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#636E72]" />
        </button>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-stone-100 border border-stone-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {vendor.logo_url ? (
              <img src={vendor.logo_url} alt={vendor.name} className="w-8 h-8 object-contain" />
            ) : (
              <span className="text-sm font-bold text-stone-400">{vendor.name?.slice(0, 2).toUpperCase()}</span>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-[#2D3436] truncate font-[Manrope]" data-testid="vendor-name">{vendor.name}</h1>
            <p className="text-xs text-[#636E72] capitalize">{vendor.sub_category?.replace(/_/g, ' ') || vendor.category}</p>
          </div>
        </div>
        {vendor.website && (
          <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-stone-100 text-[#636E72]">
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* Description */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 shadow-sm">
        <p className="text-sm text-[#636E72] leading-relaxed">{vendor.description}</p>
        {vendor.metadata?.locations && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-[#636E72]">
            <MapPin className="w-3 h-3" />
            <span>{vendor.metadata.locations}</span>
          </div>
        )}
        {vendor.metadata?.tip && (
          <p className="text-xs text-[#D64D7A] font-medium mt-2">{vendor.metadata.tip}</p>
        )}
      </div>

      {/* Products by Group */}
      {vendor.total_products === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-stone-100">
          <Package className="w-10 h-10 text-stone-300 mx-auto mb-2" />
          <p className="text-sm text-[#636E72]">No products available yet</p>
        </div>
      ) : (
        Object.entries(productGroups).map(([groupKey, products]) => (
          <div key={groupKey} className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-[#2D3436]">{PRODUCT_CATEGORY_LABELS[groupKey] || groupKey}</h2>
              <span className="text-[10px] text-[#636E72] bg-stone-100 px-2 py-0.5 rounded-full">{products.length}</span>
            </div>
            <div className="space-y-2.5">
              {products.map(product => {
                const isExpanded = expandedProduct === product.id;
                const qty = getQty(product.id);
                const isPurchasing = purchasing === product.id;

                return (
                  <div key={product.id} className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden hover:border-stone-200 transition-all" data-testid={`product-${product.id}`}>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-[#2D3436]">{product.name}</h3>
                            {product.is_popular && (
                              <span className="flex items-center gap-0.5 text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                                <Star className="w-2.5 h-2.5 fill-amber-500" /> Popular
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#636E72] mt-1">{product.description}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-base font-extrabold text-[#2D3436] font-[Manrope]">{fmt(product.price)}</p>
                        </div>
                      </div>

                      {/* Contents Toggle */}
                      {product.contents && product.contents.length > 0 && (
                        <button
                          onClick={() => setExpandedProduct(isExpanded ? null : product.id)}
                          className="flex items-center gap-1 mt-2 text-xs text-[#D64D7A] font-medium hover:underline"
                          data-testid={`toggle-contents-${product.id}`}
                        >
                          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {isExpanded ? 'Hide' : 'See'} what's included ({product.contents.length} items)
                        </button>
                      )}

                      {/* Purchase Row */}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-50">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setQty(product.id, qty - 1)} className="w-7 h-7 rounded-lg bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors" data-testid={`qty-minus-${product.id}`}>
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm font-bold w-6 text-center font-[Manrope]">{qty}</span>
                          <button onClick={() => setQty(product.id, qty + 1)} className="w-7 h-7 rounded-lg bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors" data-testid={`qty-plus-${product.id}`}>
                            <Plus className="w-3 h-3" />
                          </button>
                          {qty > 1 && <span className="text-xs text-[#636E72] ml-1">= {fmt(product.price * qty)}</span>}
                        </div>
                        <button
                          onClick={() => handlePurchase(product)}
                          disabled={isPurchasing}
                          className="flex items-center gap-1.5 px-4 py-2 bg-[#D64D7A] hover:bg-[#B52D5B] text-white rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-95 disabled:opacity-50"
                          data-testid={`buy-${product.id}`}
                        >
                          {isPurchasing ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <ShoppingCart className="w-3.5 h-3.5" />
                          )}
                          {isPurchasing ? 'Processing...' : 'Buy Now'}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Contents */}
                    {isExpanded && product.contents && (
                      <div className="bg-stone-50 border-t border-stone-100 px-4 py-3 animate-in fade-in slide-in-from-top-1 duration-200">
                        <p className="text-[10px] font-semibold text-[#636E72] uppercase tracking-wider mb-2">What's Included</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {product.contents.map((item, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-xs text-[#2D3436]">
                              <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
